/* eslint-disable no-nested-ternary */
import React, { useContext, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';

import DatePicker from '../../../Form/DatePicker';
import TimeRange from '../../../Form/TimeRange';
import FormDropdown from '../../../Form/FormDropdown';
import TextInput from '../../../Form/TextInput';
import Textarea from '../../../Form/Textarea';
import SelectedUsers from '../../SelectedUsers';
import AutoButton from '../../../AutoButton';
import UserService from '../../../../services/UserService';
import AdminService from '../../../../services/AdminService';
import { hasAuthData } from '../../../../helpers/authStorage';
import {
  getTimezoneDate,
  getTimezoneTime,
} from '../../../../helpers/timezones';
import { UiContext } from '../../../../context/UiContext';
import { JobsContext } from '../../../../context/JobsContext';

import classes from './styles.module.scss';

function formatStr(text) {
  if (!text) return '';
  return text.replace(/([a-z])([A-Z])/g, '$1-$2');
}

function getEventDate(date) {
  const tempDate = new Date(date);

  return `${tempDate.getDate()} ${tempDate.toLocaleString('en-us', {
    month: 'long',
  })} ${tempDate.getFullYear()}
  `;
}

function convertToAmPm(timeString) {
  // Split the time string into hours and minutes
  const [hours, minutes] = timeString.split(':');

  // Convert hours part to number to perform calculations
  const hoursInt = parseInt(hours, 10);

  // Determine AM or PM based on the hours
  const amPm = hoursInt >= 12 ? 'PM' : 'AM';

  // Convert 24-hour format to 12-hour format by finding the modulus of hoursInt and 12
  // and handling the special case of 0 (which should be converted to 12)
  const twelveHourFormat = hoursInt % 12 || 12;

  // Concatenate the converted hours with minutes and AM/PM suffix
  return `${twelveHourFormat}:${minutes} ${amPm}`;
}

export default function EventDetails({
  event,
  jobPost,
  jobApplication,
  refetch,
}) {
  const { t } = useTranslation();
  const { showNotification, setIsFetching, showUnknownErrorModal, showModal } =
    useContext(UiContext);
  const { showFeedbackModal, showClientRescheduleModal } =
    useContext(JobsContext);
  const isAuthorized = hasAuthData();

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: UserService.getMe,
    enabled: isAuthorized,
  });

  const isOrganizer = event?.organizer?.id === me?.id;
  const isInterviewer = event?.externalInterviewers?.some(
    (externalInterviewer) => externalInterviewer.userId === me?.id
  );
  const isInterviewerUser = event?.users?.some(
    (user) => user.userId === me?.id
  );
  const isManager = event?.companyManagers?.some(
    (manager) => manager.userId === me?.id
  );

  const externalInterviewerId = event?.externalInterviewers?.find(
    (interviewer) => interviewer.userId === me?.id
  )?.id;

  const companyManagerId = event?.companyManagers?.find(
    (manager) => manager.userId === me?.id
  )?.id;

  const internalUserId = event?.users?.find((usr) => usr.id === me?.id)?.id;

  const invitation = event?.jobApplicationInterviewInvites?.find(
    (invite) =>
      invite.externalInterviewerId === externalInterviewerId ||
      invite.companyManagerId === companyManagerId ||
      invite.userId === internalUserId
  );

  const feedback = event?.jobApplicationInterviewFeedbacks?.find(
    (fb) => fb.userId === me?.id
  );

  let adjustedStatus = event?.status;
  if (invitation?.status === 'Pending' && event?.status === 'Scheduled') {
    adjustedStatus = 'New';
  }

  if (invitation?.status === 'Pending' && event?.status === 'Rescheduled') {
    adjustedStatus = 'Rescheduled';
  }

  if (invitation?.status === 'Declined') {
    adjustedStatus = 'Declined';
  }

  if (invitation?.status === 'Accepted') {
    adjustedStatus = 'Accepted';
  }

  if (
    new Date(event?.interviewDate) < new Date() &&
    event?.status !== 'Canceled'
  ) {
    adjustedStatus = 'Completed';
  }

  if (event?.status === 'Canceled') {
    adjustedStatus = 'Canceled';
  }

  if (
    new Date(event?.interviewDate) < new Date() &&
    event?.status !== 'Canceled' &&
    !feedback
  ) {
    adjustedStatus = 'FeedbackDue';
  }

  if (
    new Date(event?.interviewDate) < new Date() &&
    event?.status !== 'Canceled' &&
    feedback
  ) {
    adjustedStatus = 'FeedbackSent';
  }

  if (
    new Date(event?.interviewDate) < new Date() &&
    event?.status !== 'Canceled' &&
    feedback?.reviewedAt != null
  ) {
    adjustedStatus = 'FeedbackFinal';
  }

  const interviewDate = useMemo(() => {
    if (!event?.interviewDate) return '';
    return getTimezoneDate(
      event.interviewDate,
      me.userProfile.timezone,
      event.startTime
    )[0];
  }, [event?.interviewDate, event?.startTime, me?.userProfile?.timezone]);

  const startTime = useMemo(() => {
    if (!event?.startTime) return '';
    return getTimezoneTime(event.startTime, me.userProfile.timezone);
  }, [event?.startTime, me?.userProfile?.timezone]);

  const endTime = useMemo(() => {
    if (!event?.endTime) return '';
    return getTimezoneTime(event.endTime, me.userProfile.timezone);
  }, [event?.endTime, me?.userProfile?.timezone]);

  const interviewTypeValue = useMemo(() => {
    if (!event?.type) return null;
    return {
      value: event.type,
      label: formatStr(event.type),
    };
  }, [event?.type]);

  const interviewers = useMemo(() => {
    if (event?.contactType === 'Client' && event?.companyManagers) {
      return event?.companyManagers?.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
        originalData: user,
      }));
    }
    if (event?.contactType === 'External' && event?.externalInterviewers) {
      return event?.externalInterviewers?.map((user) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
        originalData: user,
      }));
    }
    if (event?.users) {
      return event?.users?.map((user) => ({
        label: user.userName,
        value: user.id,
        originalData: user,
      }));
    }
    return [];
  }, [event]);

  const isExternalUserList =
    event?.contactType === 'Client' || event?.contactType === 'External';

  const interview = event;

  const declineEvent = async () => {
    const prevStatus = invitation?.status;

    const undo = async () => {
      try {
        setIsFetching(true);
        await AdminService.changeJobApplicationInterviewInvite({
          interviewId: event?.id,
          jobApplicationId: event?.jobApplicationId,
          invitationid: invitation?.id,
          note: invitation?.note,
          status: prevStatus,
        });
        if (refetch) {
          await refetch();
        }
      } catch (error) {
        showUnknownErrorModal();
      } finally {
        setIsFetching(false);
      }
    };

    showModal({
      icon: 'delete',
      title: t('pages.InterviewDashboard.declineInvitation'),
      text: t('pages.InterviewDashboard.areYouSureYouWantToDecline'),
      onConfirm: async () => {
        try {
          setIsFetching(true);
          await AdminService.changeJobApplicationInterviewInvite({
            interviewId: event?.id,
            jobApplicationId: jobApplication?.id || event?.jobApplicationId,
            invitationid: invitation?.id,
            status: 'Declined',
          });

          if (refetch) {
            await refetch();
          }
          showNotification({
            text: t(
              'dashboardComponents.Calendar.EventDetails.Interview.Interview invitation declined'
            ),
            undo,
          });
        } catch (error) {
          showUnknownErrorModal();
        } finally {
          setIsFetching(false);
        }
      },
      onCancel: () => {},
      dismissButtonLabel: t('common.no'),
      confirmButtonLabel: t('common.yes'),
    });
  };

  const acceptEvent = async () => {
    setIsFetching(true);
    try {
      await AdminService.changeJobApplicationInterviewInvite({
        interviewId: event?.id,
        jobApplicationId: jobApplication?.id || event?.jobApplicationId,
        invitationid: invitation?.id,
        note: invitation?.note,
        status: 'Accepted',
      });
      if (refetch) {
        await refetch();
      }
      showNotification({
        text: t(
          'dashboardComponents.Calendar.EventDetails.Interview.Interview invitation accepted'
        ),
      });
    } catch (error) {
      showUnknownErrorModal();
    } finally {
      setIsFetching(false);
    }

    setIsFetching(false);
  };

  if (!event) return null;

  return (
    <div className={classes.EventDetails}>
      <div className={classes.container}>
        {/* Header with Position and Action Buttons */}
        <div className={classes.header}>
          <div className={classes.positionInfo}>
            <h2 className={classes.positionTitle}>{jobPost?.jobTitle}</h2>
            <p className={classes.jobIdText}>
              {jobPost?.company?.name} | {t('common.jobId')} : {jobPost?.id}
            </p>
            {event.organizer && (
              <p className={classes.jobIdText}>
                Organizer:{' '}
                {isOrganizer ? 'You' : event?.organizer?.userProfile?.fullName}
              </p>
            )}
            {event.requestor && (
              <p className={classes.jobIdText}>
                Requestor: {event.requestor?.userProfile?.fullName}
              </p>
            )}
          </div>
          {(isInterviewer || isInterviewerUser || isManager) && invitation && (
            <div className={classes.actionButtons}>
              {adjustedStatus === 'New' || adjustedStatus === 'Rescheduled' ? (
                <AutoButton height={40} variant="black" onClick={declineEvent}>
                  {t('pages.InterviewDashboard.declineCta')}
                </AutoButton>
              ) : null}
              {isManager &&
              (adjustedStatus === 'New' ||
                adjustedStatus === 'Rescheduled' ||
                adjustedStatus === 'ChangeRequest' ||
                adjustedStatus === 'Accepted') ? (
                <AutoButton
                  height={40}
                  variant="blackWhite"
                  onClick={() =>
                    showClientRescheduleModal({
                      interview: event,
                      jobApplicationId:
                        jobApplication?.id || event?.jobApplicationId,
                      refetch,
                    })
                  }
                >
                  {t('pages.InterviewDashboard.requestChangeCta')}
                </AutoButton>
              ) : null}
              {adjustedStatus === 'New' || adjustedStatus === 'Rescheduled' ? (
                <AutoButton height={40} onClick={acceptEvent}>
                  {t('pages.InterviewDashboard.acceptCta')}
                </AutoButton>
              ) : null}
              {adjustedStatus === 'FeedbackDue' ? (
                <AutoButton
                  height={40}
                  onClick={() =>
                    showFeedbackModal({
                      candidate: jobApplication,
                      interview: event,
                      job: jobPost,
                      refetch,
                      isReviewing: false,
                      existingFeedback: null,
                    })
                  }
                >
                  {t('pages.InterviewDashboard.feedbackCta')}
                </AutoButton>
              ) : null}

              {adjustedStatus === 'FeedbackSent' ||
              adjustedStatus === 'FeedbackFinal' ? (
                <AutoButton
                  height={40}
                  variant="blackWhite"
                  onClick={() =>
                    showFeedbackModal({
                      candidate: jobApplication,
                      interview: event,
                      job: jobPost,
                      refetch,
                      isReviewing: false,
                      readOnly: true,
                      existingFeedback: feedback,
                    })
                  }
                >
                  {t('pages.InterviewDashboard.viewFeedbackCta')}
                </AutoButton>
              ) : null}
            </div>
          )}
        </div>

        {/* Interview Details Section */}
        <div className={classes.detailsSection}>
          {isManager &&
            event?.status === 'ChangeRequest' &&
            event?.suggestedInterviewDate && (
              <div className={classes.row}>
                <div className={classes.changeRequested}>
                  <h4 className={classes.changeRequestedTitle}>
                    {t(
                      'dashboardComponents.Calendar.EventDetails.Interview.changeRequestedTitle'
                    )}
                  </h4>
                  <p className={classes.changeRequestedSubtitle}>
                    {t(
                      'dashboardComponents.Calendar.EventDetails.Interview.changeRequestedSubtitle'
                    )}
                  </p>
                  <div className={classes.slotItem}>
                    <div className={classes.slotDate}>
                      {getEventDate(
                        getTimezoneDate(
                          event.suggestedInterviewDate,
                          me?.userProfile?.timezone,
                          event.suggestedStartTime
                        )[0]
                      )}
                    </div>
                    <div className={classes.slotTime}>
                      {event?.suggestedStartTime
                        ? convertToAmPm(
                            getTimezoneTime(
                              event.suggestedStartTime,
                              me?.userProfile?.timezone
                            )
                          )
                        : ''}
                      {event?.suggestedEndTime
                        ? ` - ${convertToAmPm(
                            getTimezoneTime(
                              event.suggestedEndTime,
                              me?.userProfile?.timezone
                            )
                          )}`
                        : ''}
                    </div>
                  </div>
                </div>
              </div>
            )}
          {(event?.slotDates?.length > 0 || event?.status === 'Requested') && (
            <div className={classes.row}>
              <div className={classes.slots}>
                <h4 className={classes.slotsTitle}>
                  {t('modals.ScheduleInterviewModal.suggestedSlots')}
                </h4>
                <div className={classes.slotsList}>
                  {event?.slotDates && event?.slotDates?.length > 0 ? (
                    [...event.slotDates]
                      .sort(
                        (a, b) =>
                          new Date(a.startDateTime) - new Date(b.startDateTime)
                      )
                      .map((slot) => {
                        const tzDate = getTimezoneDate(
                          slot.startDateTime,
                          me?.userProfile?.timezone
                        )[0];
                        const slotDate = getEventDate(tzDate);
                        const h = tzDate.getHours().toString().padStart(2, '0');
                        const m = tzDate
                          .getMinutes()
                          .toString()
                          .padStart(2, '0');
                        const slotTime = convertToAmPm(`${h}:${m}`);

                        return (
                          <div
                            key={slot.startDateTime}
                            className={classes.slotItem}
                          >
                            <div className={classes.slotDate}>{slotDate}</div>
                            <div className={classes.slotTime}>{slotTime}</div>
                          </div>
                        );
                      })
                  ) : (
                    <div className={classes.noSlots}>
                      {t('modals.ScheduleInterviewModal.noSlotsAvailable')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {event?.status !== 'Requested' && (
            <div className={classes.row}>
              <div className={classNames(classes.col, classes.small)}>
                <DatePicker
                  dateFieldWidth={350}
                  label={t('modals.ScheduleInterviewModal.interviewDate')}
                  name="interviewDate"
                  readonly
                  value={interviewDate}
                  setFieldValue={() => {}} // No-op function for read-only mode
                />
              </div>
              <div className={classes.col}>
                <div className={classes.timeRange}>
                  <TimeRange
                    fromValue={startTime}
                    toValue={endTime}
                    setFieldValue={() => {}} // No-op function for read-only mode
                    fromName="startTime"
                    toName="endTime"
                    readOnly
                    showDuration
                    label={t('modals.ScheduleInterviewModal.interviewTime')}
                  />
                </div>
              </div>
            </div>
          )}
          <div className={classes.row}>
            <div className={classNames(classes.col, classes.small)}>
              <FormDropdown
                name="type"
                label={t('modals.ScheduleInterviewModal.interviewMode')}
                width={170}
                height={45}
                readOnly
                value={interviewTypeValue}
                setFieldValue={() => {}} // No-op function for read-only mode
              />
            </div>
            {event?.status !== 'Requested' && (
              <div className={classes.col}>
                {event?.type === 'InPerson' ? (
                  <TextInput
                    readonly
                    name="customLocation"
                    value={event?.customLocation || ''}
                    width="100%"
                    height={45}
                    label={t('modals.ScheduleInterviewModal.Address')}
                  />
                ) : event?.type === 'Video' ? (
                  <TextInput
                    readonly
                    name="connectionDetail"
                    value={event?.connectionDetail || ''}
                    width="100%"
                    height={45}
                    label={t('modals.ScheduleInterviewModal.meetingLink')}
                  />
                ) : (
                  <TextInput
                    readonly
                    name="connectionDetail"
                    value={event?.connectionDetail || ''}
                    width="100%"
                    height={45}
                    label={t('modals.ScheduleInterviewModal.phone')}
                  />
                )}
              </div>
            )}
          </div>

          <div className={classes.row}>
            <div className={classNames(classes.col, classes.small)}>
              <div className={classes.interviewersSection}>
                <h4 className={classes.sectionLabel}>
                  {t('modals.ScheduleInterviewModal.interviewers')}
                </h4>
                <div className={classes.selectedUsers}>
                  <SelectedUsers
                    invitedUsers={interviewers}
                    readOnly
                    isExternalUserList={isExternalUserList}
                  />
                </div>
              </div>
            </div>
            <div className={classes.col}>
              <Textarea
                readonly
                name="note"
                width="100%"
                value={event?.note || ''}
                label={t('modals.ScheduleInterviewModal.emailMessage')}
                height={159}
              />
            </div>
          </div>
        </div>

        {event?.status !== 'Requested' && (
          <div className={classes.invitations}>
            {interview?.jobApplicationInterviewInvites?.map((invite) => {
              let participantName = '';
              switch (invite?.contactType) {
                case 'Internal':
                  participantName = interview.users?.find(
                    (usr) => usr.id === invite.userId
                  ).userProfile?.fullName;
                  break;
                case 'External':
                  participantName = `${
                    interview.externalInterviewers?.find(
                      (externalInterviewer) =>
                        externalInterviewer?.id === invite.externalInterviewerId
                    ).firstName
                  } ${
                    interview.externalInterviewers?.find(
                      (externalInterviewer) =>
                        externalInterviewer?.id === invite.externalInterviewerId
                    ).lastName
                  }`;
                  break;
                case 'Client':
                  participantName = `${
                    interview.companyManagers?.find(
                      (companyManager) =>
                        companyManager?.id === invite.companyManagerId
                    )?.firstName
                  } ${
                    interview.companyManagers?.find(
                      (companyManager) =>
                        companyManager?.id === invite.companyManagerId
                    )?.lastName
                  }`;
                  break;
                default:
                  participantName = jobApplication?.user?.userProfile?.fullName;
                  break;
              }
              return (
                <div className={classes.invitation} key={invite.id}>
                  <div className={classes.col}>
                    <p className={classes.title}>{participantName} </p>
                    <p
                      className={classNames(classes.description, classes.muted)}
                    >
                      {t(
                        `dashboardComponents.Candidate.AssessmentAndInterview.Interview.InterviewContactTypeOptions.${invite.contactType}`
                      )}
                    </p>
                  </div>
                  <p
                    className={classNames(classes.status, {
                      [classes.pending]: invite.status === 'Pending',
                      [classes.declined]: invite.status === 'Declined',
                      [classes.accepted]: invite.status === 'Accepted',
                    })}
                  >
                    {t(
                      `dashboardComponents.Candidate.AssessmentAndInterview.Interview.InviteStatusOptions.${invite.status}`
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
